import AppObj from 'model/appConfigObj';
import { TaskInitParams } from 'model/taskObj';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBackwardStep,
  faForwardStep,
  faMusic,
  faPause,
  faPlay,
  faRepeat,
  faShuffle,
  faStop,
  faVolumeHigh,
  faVolumeXmark,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import audioPlayerApi from './api';
import { desktopAppTaskContex } from 'screen/desktop';
import { fileBasename, isNativeAudioFile } from 'util/openableFile';
import './audioPlayer.css';

export const audioPlayerConfig = {
  appID: 'audioPlayer',
  showName: 'Music Player',
  onList: false,
  icon: require('./images/audioPlayer.png'),
  maxHead: 1,
  req: {
    minSize: { width: 320, height: 400 },
    defSize: { width: 360, height: 520 },
  },
};

type PlayerOp = 'replace' | 'append' | 'scan';

type ParsedInit = {
  op: PlayerOp;
  audioPath: string;
  audioList: string[];
  scanPaths: string[];
  nonce: string;
};

function parseJsonPaths(raw?: string): string[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((v): v is string => typeof v === 'string' && v.length > 0);
  } catch {
    return [];
  }
}

function onlyAudio(paths: string[]): string[] {
  return paths.filter((p) => isNativeAudioFile(fileBasename(p)));
}

function parseInitParams(parms?: TaskInitParams[]): ParsedInit {
  let op: PlayerOp = 'replace';
  let audioPath = '';
  let audioList: string[] = [];
  let scanPaths: string[] = [];
  let nonce = '';
  if (parms) {
    for (const p of parms) {
      if (p.command === 'op' && (p.value === 'replace' || p.value === 'append' || p.value === 'scan')) {
        op = p.value;
      }
      if (p.command === 'audioPath' && p.value) {
        audioPath = p.value;
      }
      if (p.command === 'audioList' && p.value) {
        audioList = parseJsonPaths(p.value);
      }
      if (p.command === 'scanPaths' && p.value) {
        scanPaths = parseJsonPaths(p.value);
      }
      if (p.command === 'nonce' && p.value) {
        nonce = p.value;
      }
    }
  }
  audioList = onlyAudio(audioList);
  if (audioList.length === 0 && audioPath && isNativeAudioFile(fileBasename(audioPath))) {
    audioList = [audioPath];
  }
  if (audioPath && !audioList.includes(audioPath) && isNativeAudioFile(fileBasename(audioPath))) {
    audioList = [audioPath, ...audioList];
  }
  if (!audioPath && audioList.length > 0) {
    audioPath = audioList[0];
  }
  return { op, audioPath, audioList, scanPaths, nonce };
}

function formatTime(sec: number) {
  if (!Number.isFinite(sec) || sec < 0) {
    return '00:00';
  }
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function mergeUnique(base: string[], extra: string[]): string[] {
  const seen = new Set(base);
  const out = [...base];
  for (const p of extra) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) {
    return name;
  }
  return name.slice(0, dot);
}

function parentFolderName(path: string): string {
  const cleaned = path.replace(/\/+$/, '');
  const idx = cleaned.lastIndexOf('/');
  if (idx < 0) {
    return '';
  }
  const parent = cleaned.slice(0, idx);
  const pidx = parent.lastIndexOf('/');
  return pidx >= 0 ? parent.slice(pidx + 1) : parent;
}

function mediaArtwork(src: string, type?: string): MediaImage[] {
  return (['96x96', '256x256', '512x512'] as const).map((sizes) =>
    type ? { src, sizes, type } : { src, sizes }
  );
}

function absUrl(src: string): string {
  try {
    return new URL(src, window.location.href).href;
  } catch {
    return src;
  }
}

function playerIconUrl(): string {
  return absUrl(String(audioPlayerConfig.icon));
}

function pickOtherIndex(from: number, listLen: number): number {
  if (listLen <= 1) {
    return from;
  }
  let n = Math.floor(Math.random() * (listLen - 1));
  if (n >= from) {
    n += 1;
  }
  return n;
}

const PREV_RESTART_SEC = 3;
const TICKET_RENEW_LEAD_MS = 60_000;
const TICKET_MIN_WAIT_MS = 30_000;
const TICKET_FRESH_MS = 45_000;
const TICKET_RETRY_MS = 3000;
const POSITION_STATE_MS = 1000;

type CachedTicket = {
  path: string;
  ticket: string;
  url: string;
  expiresAt: number;
  timer: number;
};

const MEDIA_SESSION_ACTIONS: MediaSessionAction[] = [
  'play',
  'pause',
  'stop',
  'previoustrack',
  'nexttrack',
  'seekto',
  'seekbackward',
  'seekforward',
];

function setMediaHandler(action: MediaSessionAction, handler: MediaSessionActionHandler | null) {
  if (!('mediaSession' in navigator)) {
    return;
  }
  try {
    navigator.mediaSession.setActionHandler(action, handler);
  } catch {
    /* unsupported action */
  }
}

function clearMediaSession() {
  if (!('mediaSession' in navigator)) {
    return;
  }
  try {
    navigator.mediaSession.metadata = null;
    navigator.mediaSession.playbackState = 'none';
  } catch {
    /* ignore */
  }
  for (const action of MEDIA_SESSION_ACTIONS) {
    setMediaHandler(action, null);
  }
}

function AudioPlayer(props: { taskInitParms?: TaskInitParams[]; taskID?: string }) {
  const [desktopControll] = useContext(desktopAppTaskContex);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const seekRef = useRef<HTMLDivElement | null>(null);
  const appliedNonce = useRef<string | null>(null);
  const scanGen = useRef(0);
  const shouldPlayRef = useRef(true);
  const repeatRef = useRef(false);
  const shuffleRef = useRef(false);
  const upcomingRef = useRef<number | null>(null);
  const historyRef = useRef<number[]>([]);
  const pathsRef = useRef<string[]>([]);
  const indexRef = useRef(0);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const ticketsRef = useRef<Map<string, CachedTicket>>(new Map());
  const inflightRef = useRef<Map<string, Promise<string>>>(new Map());
  const srcRef = useRef<string | null>(null);
  const switchingRef = useRef(false);
  const coverTypeRef = useRef<string | undefined>(undefined);
  const streamRetryRef = useRef<string | null>(null);
  const consecutiveErrorsRef = useRef(0);

  const [paths, setPaths] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(
    () => parseInitParams(props.taskInitParms).op === 'scan'
  );
  const [error, setError] = useState<string | null>(null);
  const [playFailed, setPlayFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [durations, setDurations] = useState<Record<string, number>>({});
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [limitNote, setLimitNote] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  pathsRef.current = paths;
  indexRef.current = index;
  repeatRef.current = repeat;
  shuffleRef.current = shuffle;

  const currentPath = paths[index] || '';
  const title = currentPath ? fileBasename(currentPath) : '';
  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  useEffect(() => {
    if (!props.taskID || !title) {
      return;
    }
    desktopControll?.setTaskTitle?.(props.taskID, title);
  }, [props.taskID, title, desktopControll]);

  const clearTicketTimer = (entry: CachedTicket) => {
    if (entry.timer) {
      window.clearTimeout(entry.timer);
      entry.timer = 0;
    }
  };

  const dropTicket = useCallback((path: string) => {
    const entry = ticketsRef.current.get(path);
    if (!entry) {
      return;
    }
    clearTicketTimer(entry);
    ticketsRef.current.delete(path);
    void audioPlayerApi.revokeMediaTicket(entry.ticket);
  }, []);

  const peekTicketUrl = useCallback((path: string): string | null => {
    const entry = ticketsRef.current.get(path);
    if (!entry || entry.expiresAt - Date.now() < TICKET_FRESH_MS) {
      return null;
    }
    return entry.url;
  }, []);

  const issueAndCache = useCallback(async (path: string): Promise<string> => {
    const res = await audioPlayerApi.createMediaTicket(path);
    const data = res.data?.data;
    const ticket = data?.ticket || '';
    if (!res.data?.success || !ticket) {
      throw new Error(res.data?.message || 'Cannot create playback ticket');
    }
    const url = audioPlayerApi.filesStreamUrl(ticket);
    const expiresInMs = data?.expiresInMs || 30 * 60 * 1000;
    const old = ticketsRef.current.get(path);
    if (old && old.ticket !== ticket) {
      clearTicketTimer(old);
      if (old.url !== srcRef.current) {
        void audioPlayerApi.revokeMediaTicket(old.ticket);
      }
    }
    const entry: CachedTicket = {
      path,
      ticket,
      url,
      expiresAt: Date.now() + expiresInMs,
      timer: 0,
    };
    ticketsRef.current.set(path, entry);
    const wait = Math.max(TICKET_MIN_WAIT_MS, expiresInMs - TICKET_RENEW_LEAD_MS);
    entry.timer = window.setTimeout(() => {
      void issueAndCache(path).catch(() => {});
    }, wait);
    return url;
  }, []);

  const ensureTicket = useCallback((path: string): Promise<string> => {
    const hit = peekTicketUrl(path);
    if (hit) {
      return Promise.resolve(hit);
    }
    const inflight = inflightRef.current.get(path);
    if (inflight) {
      return inflight;
    }
    const pending = issueAndCache(path).finally(() => {
      inflightRef.current.delete(path);
    });
    inflightRef.current.set(path, pending);
    return pending;
  }, [issueAndCache, peekTicketUrl]);

  const neighborPaths = useCallback((idx: number): string[] => {
    const list = pathsRef.current;
    const out: string[] = [];
    const cur = list[idx];
    if (cur) {
      out.push(cur);
    }
    if (shuffleRef.current) {
      const up = upcomingRef.current;
      if (up != null && list[up] && !out.includes(list[up])) {
        out.push(list[up]);
      }
      const prevIdx = historyRef.current[historyRef.current.length - 1];
      if (prevIdx != null && list[prevIdx] && !out.includes(list[prevIdx])) {
        out.push(list[prevIdx]);
      }
      return out;
    }
    if (idx > 0 && list[idx - 1]) {
      out.push(list[idx - 1]);
    }
    const next = idx + 1 < list.length ? list[idx + 1] : repeatRef.current ? list[0] : undefined;
    if (next && !out.includes(next)) {
      out.push(next);
    }
    return out;
  }, []);

  const prefetchNeighbors = useCallback(
    (idx: number) => {
      const keepList = neighborPaths(idx);
      const keep = new Set(keepList);
      for (let i = 0; i < keepList.length; i += 1) {
        const path = keepList[i];
        if (!peekTicketUrl(path)) {
          void ensureTicket(path).catch(() => {});
        }
      }
      Array.from(ticketsRef.current.keys()).forEach((path) => {
        if (!keep.has(path)) {
          dropTicket(path);
        }
      });
    },
    [dropTicket, ensureTicket, neighborPaths, peekTicketUrl]
  );

  const playEl = useCallback(() => {
    const el = audioRef.current;
    if (!el || !srcRef.current) {
      return;
    }
    shouldPlayRef.current = true;
    void el.play().then(() => setPlaying(true)).catch(() => setPlayFailed(true));
  }, []);

  const applyPlaybackUrl = useCallback(
    (url: string) => {
      const el = audioRef.current;
      const alreadyOnUrl = !!el && srcRef.current === url && el.getAttribute('src') === url && !el.ended;
      srcRef.current = url;
      setSrc(url);
      setPlayFailed(false);
      if (!el) {
        return;
      }
      if (!alreadyOnUrl) {
        switchingRef.current = true;
        el.src = url;
      }
      if (!shouldPlayRef.current) {
        switchingRef.current = false;
        return;
      }
      switchingRef.current = true;
      void el
        .play()
        .then(() => {
          switchingRef.current = false;
          setPlaying(true);
        })
        .catch(() => {
          switchingRef.current = false;
          setPlayFailed(true);
        });
    },
    []
  );

  const skipToIndex = useCallback(
    (next: number) => {
      const list = pathsRef.current;
      const path = list[next];
      if (!path) {
        return;
      }
      shouldPlayRef.current = true;
      indexRef.current = next;
      setIndex(next);
      setCurrentTime(0);
      setDuration(0);
      const url = peekTicketUrl(path);
      if (url) {
        applyPlaybackUrl(url);
      }
      prefetchNeighbors(next);
    },
    [applyPlaybackUrl, peekTicketUrl, prefetchNeighbors]
  );

  const applyReplace = useCallback((list: string[], startPath: string) => {
    const next = onlyAudio(list);
    const start = startPath && next.includes(startPath) ? startPath : next[0] || '';
    setPaths(next);
    setIndex(start ? Math.max(0, next.indexOf(start)) : 0);
    setLimitNote(null);
    setError(null);
    setPlayFailed(false);
    shouldPlayRef.current = next.length > 0;
    historyRef.current = [];
    upcomingRef.current = null;
    const el = audioRef.current;
    if (el && start && start === pathsRef.current[indexRef.current]) {
      el.currentTime = 0;
      playEl();
    }
  }, [playEl]);

  const applyAppend = useCallback((list: string[]) => {
    const extra = onlyAudio(list);
    if (extra.length === 0) {
      return;
    }
    const prev = pathsRef.current;
    const next = mergeUnique(prev, extra);
    const firstNew = extra.find((p) => !prev.includes(p));
    setPaths(next);
    // Only start the newly added tracks when the playlist was empty.
    // If something is already queued or playing, keep the current index.
    if (firstNew && prev.length === 0) {
      setIndex(Math.max(0, next.indexOf(firstNew)));
      shouldPlayRef.current = true;
    }
  }, []);

  useEffect(() => {
    const parsed = parseInitParams(props.taskInitParms);
    const nonce = parsed.nonce || 'init';
    if (appliedNonce.current === nonce) {
      return;
    }
    appliedNonce.current = nonce;
    scanGen.current += 1;
    const gen = scanGen.current;
    setScanLoading(parsed.op === 'scan');

    if (parsed.op === 'replace') {
      applyReplace(parsed.audioList, parsed.audioPath);
      return;
    }
    if (parsed.op === 'append') {
      applyAppend(parsed.audioList);
      return;
    }
    if (parsed.op !== 'scan') {
      return;
    }
    const scanPaths = parsed.scanPaths;
    if (scanPaths.length === 0) {
      setScanLoading(false);
      setError('No paths to scan');
      return;
    }
    setError(null);
    void audioPlayerApi
      .scanMusic(scanPaths)
      .then((res) => {
        if (gen !== scanGen.current) {
          return;
        }
        const data = res.data?.data;
        if (!res.data?.success || !data) {
          throw new Error(res.data?.message || 'Scan failed');
        }
        const found = onlyAudio(data.paths || []);
        if (found.length === 0) {
          setError('No supported music files found');
          return;
        }
        applyAppend(found);
        if (data.truncated) {
          setLimitNote('Playlist limited to 1000 tracks. Extra files were skipped.');
        }
      })
      .catch((e: any) => {
        if (gen !== scanGen.current) {
          return;
        }
        setError(e?.response?.data?.message || e?.message || 'Failed to scan music');
      })
      .finally(() => {
        if (gen === scanGen.current) {
          setScanLoading(false);
        }
      });
  }, [props.taskInitParms, applyReplace, applyAppend]);

  useEffect(() => {
    if (!currentPath) {
      srcRef.current = null;
      setSrc(null);
      setPlaying(false);
      const el = audioRef.current;
      if (el) {
        el.removeAttribute('src');
        el.load();
      }
      return;
    }
    let cancelled = false;
    let retryTimer = 0;
    const cached = peekTicketUrl(currentPath);
    if (cached && srcRef.current === cached) {
      setLoading(false);
      prefetchNeighbors(indexRef.current);
      return;
    }
    if (!cached) {
      setLoading(true);
    }
    setError(null);
    setPlayFailed(false);
    if (srcRef.current !== cached) {
      setCurrentTime(0);
      setDuration(0);
    }
    const path = currentPath;
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        retryTimer = window.setTimeout(resolve, ms);
      });
    void (async () => {
      while (!cancelled) {
        try {
          const url = await ensureTicket(path);
          if (cancelled) {
            return;
          }
          applyPlaybackUrl(url);
          setLoading(false);
          prefetchNeighbors(indexRef.current);
          return;
        } catch {
          if (cancelled) {
            return;
          }
          setLoading(true);
          await sleep(TICKET_RETRY_MS);
        }
      }
    })();
    prefetchNeighbors(indexRef.current);
    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [currentPath, applyPlaybackUrl, ensureTicket, peekTicketUrl, prefetchNeighbors]);

  useEffect(() => {
    return () => {
      Array.from(ticketsRef.current.keys()).forEach((path) => {
        dropTicket(path);
      });
    };
  }, [dropTicket]);

  useEffect(() => {
    const ac = new AbortController();
    if (!currentPath) {
      coverTypeRef.current = undefined;
      setCoverUrl(null);
      return;
    }
    setCoverUrl(null);
    coverTypeRef.current = undefined;
    void audioPlayerApi
      .fetchThumbnails([currentPath], ac.signal)
      .then((data) => {
        if (ac.signal.aborted) {
          return;
        }
        const item = data?.items?.[0];
        if (!item || item.skipped || !item.dataBase64 || !item.contentType) {
          setCoverUrl(null);
          return;
        }
        coverTypeRef.current = item.contentType;
        setCoverUrl(`data:${item.contentType};base64,${item.dataBase64}`);
      })
      .catch(() => {
        if (!ac.signal.aborted) {
          coverTypeRef.current = undefined;
          setCoverUrl(null);
        }
      });
    return () => {
      ac.abort();
    };
  }, [currentPath]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) {
      return;
    }
    el.volume = muted ? 0 : volume;
  }, [volume, muted, src]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !src) {
      return;
    }
    if (shouldPlayRef.current) {
      playEl();
    }
  }, [src, playEl]);

  useEffect(() => {
    const btn = currentPath ? rowRefs.current.get(currentPath) : undefined;
    btn?.scrollIntoView({ block: 'nearest' });
  }, [currentPath, paths.length]);

  useEffect(() => {
    if (!shuffle || paths.length <= 1) {
      if (!shuffle) {
        upcomingRef.current = null;
      }
      return;
    }
    const cur = indexRef.current;
    const up = upcomingRef.current;
    if (up == null || up === cur || up < 0 || up >= paths.length) {
      upcomingRef.current = pickOtherIndex(cur, paths.length);
    }
    prefetchNeighbors(cur);
  }, [shuffle, currentPath, paths.length, prefetchNeighbors]);

  const goPrev = useCallback(() => {
    const el = audioRef.current;
    if (el && el.currentTime > PREV_RESTART_SEC) {
      el.currentTime = 0;
      setCurrentTime(0);
      if (shouldPlayRef.current) {
        playEl();
      }
      return;
    }
    const list = pathsRef.current;
    const i = indexRef.current;
    if (shuffleRef.current) {
      const prev = historyRef.current.pop();
      if (prev != null && prev >= 0 && prev < list.length) {
        upcomingRef.current = i;
        skipToIndex(prev);
        return;
      }
      if (el) {
        el.currentTime = 0;
        setCurrentTime(0);
      }
      return;
    }
    if (i <= 0) {
      if (el) {
        el.currentTime = 0;
        setCurrentTime(0);
      }
      return;
    }
    skipToIndex(i - 1);
  }, [playEl, skipToIndex]);

  const goNext = useCallback(() => {
    const list = pathsRef.current;
    const i = indexRef.current;
    if (shuffleRef.current && list.length > 1) {
      let dest = upcomingRef.current;
      if (dest == null || dest === i || dest < 0 || dest >= list.length) {
        dest = pickOtherIndex(i, list.length);
      }
      historyRef.current.push(i);
      if (historyRef.current.length > 64) {
        historyRef.current.shift();
      }
      upcomingRef.current = pickOtherIndex(dest, list.length);
      skipToIndex(dest);
      return;
    }
    if (i < list.length - 1) {
      skipToIndex(i + 1);
      return;
    }
    if (repeatRef.current && list.length > 0) {
      skipToIndex(0);
      return;
    }
    shouldPlayRef.current = false;
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setPlaying(false);
  }, [skipToIndex]);

  const pauseEl = useCallback(() => {
    const el = audioRef.current;
    shouldPlayRef.current = false;
    if (el) {
      el.pause();
    }
    setPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !srcRef.current) {
      return;
    }
    if (el.paused) {
      playEl();
    } else {
      pauseEl();
    }
  }, [playEl, pauseEl]);

  const stop = useCallback(() => {
    const el = audioRef.current;
    shouldPlayRef.current = false;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setPlaying(false);
    setCurrentTime(0);
  }, []);

  const seekToRatio = useCallback((ratio: number) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) {
      return;
    }
    const next = Math.min(1, Math.max(0, ratio)) * el.duration;
    el.currentTime = next;
    setCurrentTime(next);
  }, []);

  const seekBy = useCallback((deltaSec: number) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) {
      return;
    }
    const next = Math.min(el.duration, Math.max(0, el.currentTime + deltaSec));
    el.currentTime = next;
    setCurrentTime(next);
  }, []);

  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }
    if (!currentPath) {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
      } catch {
        /* ignore */
      }
      return;
    }
    const base = fileBasename(currentPath);
    const artSrc = coverUrl ? absUrl(coverUrl) : playerIconUrl();
    const artType = coverUrl ? coverTypeRef.current : 'image/png';
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: stripExtension(base) || base || 'Music Player',
        artist: 'Music Player',
        album: parentFolderName(currentPath) || 'Music Player',
        artwork: mediaArtwork(artSrc, artType),
      });
    } catch {
      /* ignore */
    }
    try {
      const live = switchingRef.current || playing;
      navigator.mediaSession.playbackState = live ? 'playing' : 'paused';
    } catch {
      /* ignore */
    }
  }, [currentPath, coverUrl, playing]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !src) {
      return;
    }
    const push = () => {
      const el = audioRef.current;
      if (!el) {
        return;
      }
      const d = el.duration;
      if (!Number.isFinite(d) || d <= 0) {
        return;
      }
      try {
        navigator.mediaSession.setPositionState({
          duration: d,
          playbackRate: el.playbackRate || 1,
          position: Math.min(d, Math.max(0, el.currentTime)),
        });
      } catch {
        /* unsupported */
      }
    };
    push();
    if (!playing) {
      return;
    }
    const id = window.setInterval(push, POSITION_STATE_MS);
    return () => window.clearInterval(id);
  }, [src, duration, playing]);

  const playElRef = useRef(playEl);
  const pauseElRef = useRef(pauseEl);
  const stopRef = useRef(stop);
  const goPrevRef = useRef(goPrev);
  const goNextRef = useRef(goNext);
  const seekToRatioRef = useRef(seekToRatio);
  const seekByRef = useRef(seekBy);
  playElRef.current = playEl;
  pauseElRef.current = pauseEl;
  stopRef.current = stop;
  goPrevRef.current = goPrev;
  goNextRef.current = goNext;
  seekToRatioRef.current = seekToRatio;
  seekByRef.current = seekBy;

  const bindMediaHandlers = useCallback(() => {
    setMediaHandler('play', () => playElRef.current());
    setMediaHandler('pause', () => pauseElRef.current());
    setMediaHandler('stop', () => stopRef.current());
    setMediaHandler('previoustrack', () => {
      goPrevRef.current();
      const el = audioRef.current;
      if (el && shouldPlayRef.current) {
        return el.play().catch(() => undefined);
      }
    });
    setMediaHandler('nexttrack', () => {
      goNextRef.current();
      const el = audioRef.current;
      if (el && shouldPlayRef.current) {
        return el.play().catch(() => undefined);
      }
    });
    setMediaHandler('seekto', (details) => {
      if (details.seekTime == null) {
        return;
      }
      const el = audioRef.current;
      if (!el || !Number.isFinite(el.duration) || el.duration <= 0) {
        return;
      }
      seekToRatioRef.current(details.seekTime / el.duration);
    });
    setMediaHandler('seekbackward', (details) => {
      seekByRef.current(-(details.seekOffset ?? 10));
    });
    setMediaHandler('seekforward', (details) => {
      seekByRef.current(details.seekOffset ?? 10);
    });
  }, []);

  useEffect(() => {
    bindMediaHandlers();
    return () => {
      clearMediaSession();
    };
  }, [bindMediaHandlers]);

  useEffect(() => {
    bindMediaHandlers();
  }, [bindMediaHandlers, currentPath, playing]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      bindMediaHandlers();
      const el = audioRef.current;
      if (shouldPlayRef.current && el?.src && el.paused) {
        void el.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [bindMediaHandlers]);

  const onSeekPointer = (ev: React.PointerEvent<HTMLDivElement>) => {
    const bar = seekRef.current;
    if (!bar) {
      return;
    }
    const pick = (clientX: number) => {
      const rect = bar.getBoundingClientRect();
      seekToRatio(rect.width <= 0 ? 0 : (clientX - rect.left) / rect.width);
    };
    pick(ev.clientX);
    bar.setPointerCapture(ev.pointerId);
    const move = (e: PointerEvent) => pick(e.clientX);
    const up = () => {
      bar.releasePointerCapture(ev.pointerId);
      bar.removeEventListener('pointermove', move);
      bar.removeEventListener('pointerup', up);
    };
    bar.addEventListener('pointermove', move);
    bar.addEventListener('pointerup', up);
  };

  const empty = paths.length === 0 && !scanLoading;

  return (
    <div className="ap-root" onClick={() => setShowVolume(false)}>
      <audio
        ref={audioRef}
        className="ap-hidden-audio"
        playsInline
        preload="auto"
        onPlay={() => {
          streamRetryRef.current = null;
          consecutiveErrorsRef.current = 0;
          setPlaying(true);
          setPlayFailed(false);
        }}
        onPause={() => {
          const el = audioRef.current;
          if (switchingRef.current || el?.ended) {
            return;
          }
          setPlaying(false);
        }}
        onTimeUpdate={(e) => {
          setCurrentTime(e.currentTarget.currentTime);
        }}
        onDurationChange={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) {
            setDuration(d);
            if (currentPath) {
              setDurations((prev) => (prev[currentPath] === d ? prev : { ...prev, [currentPath]: d }));
            }
          }
        }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) {
            setDuration(d);
            if (currentPath) {
              setDurations((prev) => ({ ...prev, [currentPath]: d }));
            }
          }
        }}
        onEnded={() => {
          goNext();
        }}
        onError={() => {
          consecutiveErrorsRef.current += 1;
          const path = pathsRef.current[indexRef.current];
          if (path && streamRetryRef.current !== path && shouldPlayRef.current) {
            streamRetryRef.current = path;
            dropTicket(path);
            void (async () => {
              while (shouldPlayRef.current && pathsRef.current[indexRef.current] === path) {
                try {
                  const url = await ensureTicket(path);
                  if (pathsRef.current[indexRef.current] !== path) {
                    return;
                  }
                  applyPlaybackUrl(url);
                  return;
                } catch {
                  await new Promise<void>((resolve) => {
                    window.setTimeout(resolve, TICKET_RETRY_MS);
                  });
                }
              }
            })();
            return;
          }
          if (shouldPlayRef.current && consecutiveErrorsRef.current < 8) {
            const list = pathsRef.current;
            const i = indexRef.current;
            const hasNext =
              shuffleRef.current && list.length > 1
                ? true
                : i < list.length - 1 || (repeatRef.current && list.length > 1);
            if (hasNext) {
              goNext();
              return;
            }
          }
          shouldPlayRef.current = false;
          setPlayFailed(true);
        }}
      />

      <div className="ap-now">
        <div className={`ap-art${loading ? ' loading' : ''}${coverUrl ? ' has-cover' : ''}`} aria-hidden>
          {coverUrl ? (
            <img className="ap-art-img" src={coverUrl} alt="" draggable={false} />
          ) : (
            <>
              <span className="ap-art-facet ap-art-facet-a" />
              <span className="ap-art-facet ap-art-facet-b" />
              <FontAwesomeIcon icon={faMusic} />
            </>
          )}
        </div>
        <div className="ap-now-main">
          <div className="ap-now-title" title={currentPath || undefined}>
            {title || 'Music Player'}
          </div>
          <div className="ap-controls">
            <div className="ap-vol-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="ap-icon-btn"
                title={muted || volume === 0 ? 'Unmute' : 'Volume'}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVolume((v) => !v);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMuted((m) => !m);
                }}
              >
                <FontAwesomeIcon icon={muted || volume === 0 ? faVolumeXmark : faVolumeHigh} />
              </button>
              {showVolume && (
                <div className="ap-vol-pop" onPointerDown={(e) => e.stopPropagation()}>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={muted ? 0 : volume}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setVolume(v);
                      setMuted(v === 0);
                    }}
                  />
                </div>
              )}
            </div>
            <button
              type="button"
              className="ap-icon-btn"
              title="Previous"
              onClick={goPrev}
              disabled={!shuffle && index <= 0}
            >
              <FontAwesomeIcon icon={faBackwardStep} />
            </button>
            <button type="button" className="ap-icon-btn ap-icon-main" title={playing ? 'Pause' : 'Play'} onClick={togglePlay} disabled={!src}>
              <FontAwesomeIcon icon={playing ? faPause : faPlay} />
            </button>
            <button
              type="button"
              className="ap-icon-btn"
              title="Next"
              onClick={goNext}
              disabled={shuffle ? paths.length <= 1 && !repeat : index >= paths.length - 1 && !repeat}
            >
              <FontAwesomeIcon icon={faForwardStep} />
            </button>
            <button type="button" className="ap-icon-btn" title="Stop" onClick={stop} disabled={!src}>
              <FontAwesomeIcon icon={faStop} />
            </button>
          </div>
          <div className="ap-time-row">
            <span className="ap-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button
              type="button"
              className={`ap-icon-btn ap-repeat${repeat ? ' on' : ''}`}
              title={repeat ? 'Repeat on' : 'Repeat off'}
              onClick={() => setRepeat((r) => !r)}
            >
              <FontAwesomeIcon icon={faRepeat} />
            </button>
            <button
              type="button"
              className={`ap-icon-btn ap-shuffle${shuffle ? ' on' : ''}`}
              title={shuffle ? 'Shuffle on' : 'Shuffle off'}
              onClick={() => setShuffle((s) => !s)}
            >
              <FontAwesomeIcon icon={faShuffle} />
            </button>
          </div>
        </div>
      </div>

      <div
        className="ap-seek"
        ref={seekRef}
        onPointerDown={onSeekPointer}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration) || 0}
        aria-valuenow={Math.round(currentTime)}
        aria-label="Seek"
      >
        <div className="ap-seek-fill" style={{ width: `${progress * 100}%` }} />
        <div className="ap-seek-thumb" style={{ left: `${progress * 100}%` }} />
      </div>

      {limitNote && (
        <div className="ap-banner">
          <span>{limitNote}</span>
          <button type="button" className="ap-banner-close" onClick={() => setLimitNote(null)} title="Dismiss">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      <div className="ap-list">
        {empty && <div className="ap-empty-list">No audio in playlist</div>}
        {paths.map((path) => {
          const active = path === currentPath;
          const name = fileBasename(path);
          const dur = durations[path];
          return (
            <button
              key={path}
              type="button"
              className={`ap-row${active ? ' active' : ''}`}
              title={path}
              ref={(el) => {
                if (el) {
                  rowRefs.current.set(path, el);
                } else {
                  rowRefs.current.delete(path);
                }
              }}
              onClick={() => {
                if (path === currentPath) {
                  togglePlay();
                  return;
                }
                shouldPlayRef.current = true;
                setIndex(paths.indexOf(path));
              }}
            >
              <span className="ap-row-name">{name}</span>
              <span className="ap-row-dur">{dur != null ? formatTime(dur) : '--:--'}</span>
            </button>
          );
        })}
      </div>

      {scanLoading && (
        <div className="ap-overlay">
          <div className="ap-spinner" />
          <div>Loading playlist…</div>
        </div>
      )}
      {error && !scanLoading && (
        <div className="ap-overlay ap-overlay-error">
          <p>{error}</p>
          <button type="button" className="ap-overlay-ok" onClick={() => setError(null)}>
            OK
          </button>
        </div>
      )}
      {playFailed && !loading && !error && !scanLoading && (
        <div className="ap-overlay ap-overlay-error">
          <p>This audio cannot be played in the browser.</p>
          <button type="button" className="ap-overlay-ok" onClick={() => setPlayFailed(false)}>
            OK
          </button>
        </div>
      )}
    </div>
  );
}

const App: AppObj = {
  config: audioPlayerConfig,
  body: AudioPlayer,
};

export default App;
